import React, { useContext, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

import API from "api/API";
import AuthAPI from 'api/AuthAPI';
import Storage from 'utils/Storage';
import Loader from 'components/general/Loader';

type AuthContextValue = {
   user: AuthUser;
   updateUser: () => Promise<void>;
   logIn: ( dto: LogInDTO ) => Promise<void>;
   logOut: () => void;
}

const AuthContext = React.createContext({} as AuthContextValue );

export default function AuthContextProvider() {

	const [ loading, setLoading ] = useState( true );
	const [ user, setUser ] = useState<AuthUser | null>( null );

	const getUser = async() => {
		try {
			const data = await AuthAPI.me();
         setUser( data );
			setTimeout(() => setLoading( false ), 300 );
		} catch ( e ) {
			return Promise.reject( e );
		}
	}

	const logIn = async( dto: LogInDTO ) => {
		const data = await AuthAPI.logIn( dto );
      Storage.set( "access_token", data.access_token );
		getUser();
	}

	const logOut = async() => {

      if ( Storage.get( "access_token" )) {
         try {
            await AuthAPI.logOut();
         } catch {}
      }
      
		Storage.remove( "access_token" );
		Storage.remove( "refresh_token" );
		Storage.remove( "token_expires_at" );

		setUser( null );
		setLoading( false );
	}

	useEffect(() => {

		API.interceptors.response.use( res => {
			if ( res?.data?.code === 401 ) logOut();
			return res;
		}, err => {
			const resp = err?.response;
			if ( resp?.status === 401 && !/\/logout/.test( resp?.config?.url )) logOut();
			return Promise.reject( err );
		});
	}, [])

	useEffect(() => {

		const access_token = Storage.get( "access_token" );
		
		if ( access_token ) {
			getUser()
		} else {
			setLoading( false );
		}
	}, [])

	return loading ? <Loader style={{ height: "100vh" }}/> : (
		<AuthContext.Provider value={{
			user: user || {} as AuthUser,
         updateUser: getUser,
			logIn,
			logOut
		}}>
			<Outlet/>
		</AuthContext.Provider>
	);
};

export const useAuthContext = () => useContext( AuthContext );

export const useUser = () => useAuthContext().user;

export const useLogOut = () => useAuthContext().logOut;

export const useLogIn = () => useAuthContext().logIn;